import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslateLoader } from '@ngx-translate/core';
import { ComponentTranslationModel } from '../models/component-translation.model';
import { LogService } from './log.service';
import { PershingJsonUtil } from '../utils/pershing-json.util';
@Injectable()
export class PershingTranslateLoader implements TranslateLoader {

    private prefix: string = 'i18n';
    private suffix: string = '.json';
    private delimiter: string = '~~';
    private _componentList: ComponentTranslationModel[] = [];
    private queue: string [][] = [];
    private translationOverride: string [][] = [];

    constructor(private http: HttpClient,
                private logService: LogService) {
    }

    addComponentList(nameInput: string, pathInput: string[], parentKey: string = '') {
        this._componentList.push(new ComponentTranslationModel({name: nameInput, path: pathInput, parentKey: parentKey}));
    }

    existComponent(name: string, parentKey: string = ''): boolean {
        return this._componentList.find(x => x.name === name && x.parentKey === parentKey) ? true : false;
    }

    getComponentToFetch(lang: string) {
        let observableBatch = [];
        this._componentList.forEach((component) => {
            let nameInQueue: string = `${component.name}${this.delimiter}${component.parentKey}`;
            if (!this.isComponentInQueue(lang, nameInQueue)) {
                this.queue[lang].push(nameInQueue);
                const jsonRequests = component.path.map(resource => {
                    return this.http.get(`${resource}/${this.prefix}/${lang}${this.suffix}`, { responseType: 'json' })
                        .pipe(catchError(() => {
                            // Empty Observable just to go ahead
                            return of([]);
                        }));
                });

                observableBatch.push(forkJoin(jsonRequests).pipe(map(response => {
                    // if response has mutiple objects merge
                    // else assign the response to component.json
                    if (response.length > 1) {
                        let mergedResponse = PershingJsonUtil.mergeAll(response);
                        component.json[lang] = mergedResponse;
                    } else {
                        component.json[lang] = response[0];
                    }
                })));
            }
        });
        return observableBatch;
    }

    init(lang: string) {
        if (this.queue[lang] === undefined) {
            this.queue[lang] = [];
        }
    }

    isComponentInQueue(lang: string, nameInQueue: string) {
        return this.queue[lang].find(x => x === nameInQueue) ? true : false;
    }

    getFullTranslationJSON(lang: string) {
       //  let fullTranslation: string = '';
        let resTranslation: any = undefined;
        let cloneList = this._componentList.slice(0);
        cloneList.reverse().forEach((component) => {
            if (component.json && component.json[lang]) {
                let componentJson = component.json[lang];
                if (component.parentKey !== '') {
                    let identifiedJson = {};
                    identifiedJson[component.parentKey] = component.json[lang];
                    componentJson = identifiedJson;
                }
                if (Object.keys(componentJson).length > 0) {
                    if (resTranslation) {
                        resTranslation = PershingJsonUtil.mergeDeep(resTranslation, componentJson);
                    } else {
                        resTranslation = Object.assign( {}, componentJson);
                    }
                }
            }
        });
        if (this.translationOverride[lang]) {
            this.translationOverride[lang].forEach(override => {
                if (Object.keys(override).length > 0) {
                    if (resTranslation) {
                        resTranslation = PershingJsonUtil.mergeDeep(resTranslation, override);
                    } else {
                        resTranslation = Object.assign( {}, override);
                    }
                }
            });
        }
        if (resTranslation) {
            return resTranslation;
        }
    }

    getTranslation(lang: string): Observable<any> {
        let observableBatch = this.getComponentToFetch(lang);
        return Observable.create(observer => {
            if (observableBatch.length > 0) {
                forkJoin(observableBatch).subscribe(
                    () => {
                        let fullTranslation = this.getFullTranslationJSON(lang);
                        if (fullTranslation) {
                            observer.next(fullTranslation);
                        }
                        observer.complete();
                    },
                    (err: any) => {
                        this.logService.error(err);
                    });
            } else {
                let fullTranslation = this.getFullTranslationJSON(lang);
                if (fullTranslation) {
                    observer.next(fullTranslation);
                }
            }
        });
    }

    addTranslationOverride(lang: string, override: Object) {
        if (this.translationOverride[lang] === undefined) {
            this.translationOverride[lang] = [];
        }
        this.translationOverride[lang].push(override);
    }

}
